export const PRIVACY_UPDATED = "28 June 2026";

export const PRIVACY_INTRO = [
  "This Privacy Notice for Harman AI Solutions (\"we\", \"us\", or \"our\") describes how and why we might collect, store, use, and/or share (\"process\") your information when you use our services (\"Services\"), such as when you visit our website at https://harmanaisolutions.com or contact us.",
  "If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at team@harmanaisolutions.com.",
];

export const PRIVACY_KEY_POINTS = [
  { label: "What personal information do we process?", text: "We may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use." },
  { label: "Do we process any sensitive personal information?", text: "We do not process sensitive personal information." },
  { label: "Do we collect any information from third parties?", text: "We do not collect any information from third parties." },
  { label: "How do we process your information?", text: "We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law." },
  { label: "In what situations do we share information?", text: "We may share information in specific situations and with specific third parties." },
  { label: "How do we keep your information safe?", text: "We have adequate organisational and technical processes and procedures in place to protect your personal information." },
  { label: "What are your rights?", text: "Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information." },
  { label: "How do you exercise your rights?", text: "The easiest way to exercise your rights is by visiting our contact page, or by contacting us." },
];

export const PRIVACY_SECTIONS = [
  {
    title: "1. What Information Do We Collect?",
    paras: [
      "We collect personal information that you voluntarily provide to us when you express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.",
      "We do not process sensitive information.",
      "Our use of information received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements.",
    ],
    subsections: [
      {
        heading: "Personal information provided by you",
        bullets: ["Names", "Email addresses", "Contact preferences", "Business name", "Country"],
      },
      {
        heading: "Information automatically collected",
        paras: ["Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics — is collected automatically when you visit our Services."],
        bullets: ["Log and Usage Data", "Device Data"],
      },
    ],
  },
  {
    title: "2. How Do We Process Your Information?",
    paras: ["We process your personal information for a variety of reasons, including:"],
    bullets: [
      "To deliver and facilitate delivery of services to the user.",
      "To respond to user enquiries and offer support to users.",
      "To fulfil and manage your orders.",
      "To request feedback.",
      "To protect our Services.",
      "To identify usage trends.",
      "To save or protect an individual's vital interest.",
    ],
  },
  {
    title: "3. What Legal Bases Do We Rely On To Process Your Information?",
    paras: ["We only process your personal information when we believe it is necessary and we have a valid legal reason to do so. We rely on the following legal bases:"],
    bullets: ["Consent.", "Performance of a Contract.", "Legitimate Interests.", "Legal Obligations.", "Vital Interests."],
  },
  {
    title: "4. When And With Whom Do We Share Your Personal Information?",
    paras: ["We may share information in specific situations described in this section and/or with the following third parties:"],
    bullets: ["Web and Mobile Analytics: Google Analytics"],
    subsections: [
      {
        heading: "Business Transfers",
        paras: ["We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company."],
      },
    ],
  },
  {
    title: "5. Do We Use Cookies And Other Tracking Technologies?",
    paras: [
      "We may use cookies and similar tracking technologies to collect and store your information.",
      "We may share your information with Google Analytics to track and analyse the use of the Services.",
    ],
  },
  {
    title: "6. Is Your Information Transferred Internationally?",
    paras: ["We may transfer, store, and process your information in countries other than your own."],
  },
  {
    title: "7. How Long Do We Keep Your Information?",
    paras: ["We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice."],
  },
  {
    title: "8. How Do We Keep Your Information Safe?",
    paras: ["We have implemented appropriate and reasonable technical and organisational security measures designed to protect the security of any personal information we process."],
  },
  {
    title: "9. Do We Collect Information From Minors?",
    paras: ["We do not knowingly collect, solicit data from, or market to children under 18 years of age."],
  },
  {
    title: "10. What Are Your Privacy Rights?",
    paras: [
      "Depending on your state of residence in the US or in some regions, such as the European Economic Area (EEA), United Kingdom (UK), Switzerland, and Canada, you have rights that allow you greater access to and control over your personal information.",
      "The easiest way to exercise your rights is by visiting our contact page, or by contacting us using the details provided below.",
    ],
  },
  {
    title: "11. Controls For Do-Not-Track Features",
    paras: ["We do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online."],
  },
  {
    title: "12. Do United States Residents Have Specific Privacy Rights?",
    paras: ["If you are a resident of certain states, you may have the right to request access to and receive details about the personal information we maintain about you."],
  },
  {
    title: "13. Do We Make Updates To This Notice?",
    paras: ["We may update this Privacy Notice from time to time. The updated version will be indicated by an updated date at the top of this Privacy Notice."],
  },
  {
    title: "14. How Can You Contact Us About This Notice?",
    paras: ["You may contact our Data Protection Officer (DPO) by email, or by post:"],
    contact: {
      name: "Harman AI Solutions — Data Protection Officer",
      address: "Edinburgh, Scotland, United Kingdom",
      email: "team@harmanaisolutions.com",
      website: "https://harmanaisolutions.com",
    },
  },
  {
    title: "15. How Can You Review, Update, Or Delete The Data We Collect From You?",
    paras: ["To request to review, update, or delete your personal information, please visit our contact page at https://harmanaisolutions.com/contact."],
  },
];
